import React, { useState, useEffect } from 'react';
import { getToken } from '../utils/getToken';
import AllPatient from '../component/patient/AllPatient';
import { NumberTo2DP } from '../utils/NumberTo2DP';
import PatientCharts from '../component/PatientChart';

const baseURL = process.env.REACT_APP_BASE_URL || 'https://api.quickcare.ng/admin';

const Patients = () => {
    const [totalPatients, setTotalPatients] = useState(0);
    const [signupsThisMonth, setSignupsThisMonth] = useState(0);
    const [activePatients, setActivePatients] = useState(0);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchStats = async () => {
            const myHeaders = new Headers();
            myHeaders.append("Accept", "application/json");

            const token = getToken();
            if (token) {
                myHeaders.append("Authorization", `Bearer ${token}`);
            } else {
                console.error('No token found');
                return;
            }

            const requestOptions = {
                method: "GET",
                headers: myHeaders,
                redirect: "follow"
            };

            try {
                const response = await fetch(`${baseURL}/patients/stats`, requestOptions);
                const result = await response.json();
                console.log('Server response:', result);

                const { signups_this_month, total_customers, active_customers } = result.data;
                setTotalPatients(total_customers);
                setSignupsThisMonth(signups_this_month);
                setActivePatients(active_customers);
                setLoading(false);
            } catch (error) {
                console.error('Error fetching patient stats:', error);
                setLoading(false);
            }
        };

        fetchStats();
    }, []);

    // Card configurations
    const cardConfigs = [
        {
            id: 1,
            title: 'Total Patients',
            value: totalPatients,
            icon: '👥',
            bgColor: 'bg-blue-50',
            textColor: 'text-blue-600',
            borderColor: 'border-blue-100',
            hoverColor: 'hover:border-blue-300',
            subtitle: 'All registered patients'
        },
        {
            id: 2,
            title: 'Active Patients',
            value: activePatients,
            icon: '✅',
            bgColor: 'bg-green-50',
            textColor: 'text-green-600',
            borderColor: 'border-green-100',
            hoverColor: 'hover:border-green-300',
            subtitle: 'Currently active'
        },
        {
            id: 3,
            title: 'Sign-Ups This Month',
            value: signupsThisMonth,
            icon: '📈',
            bgColor: 'bg-purple-50',
            textColor: 'text-purple-600',
            borderColor: 'border-purple-100',
            hoverColor: 'hover:border-purple-300',
            subtitle: 'New patients this month'
        }
    ];

    return (
        <div className='w-full px-3 sm:px-4 lg:px-6 py-4 lg:py-6 bg-gradient-to-br from-gray-50 to-gray-100/50 min-h-screen mt-8 lg:mt-0'>
            {/* Page Header */}
            <div className='mb-6'>
                <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3'>
                    <div>
                        <h1 className='text-2xl lg:text-3xl font-bold text-gray-800 flex items-center gap-2'>
                            <span>🏥</span> Patients Dashboard
                        </h1>
                        <p className='text-sm text-gray-500 mt-1'>
                            Manage and monitor your patient database
                        </p>
                    </div>
                    <div className='flex items-center gap-2 text-sm text-gray-500 bg-white px-4 py-2 rounded-xl shadow-sm'>
                        <span className='inline-block w-2 h-2 bg-green-500 rounded-full animate-pulse'></span>
                        Live Data
                    </div>
                </div>
            </div>

            {/* Stats Cards */}
            <div className='mb-6'>
                <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4'>
                    {cardConfigs.map((card) => (
                        <div 
                            key={card.id}
                            className={`group bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 border ${card.borderColor} ${card.hoverColor} overflow-hidden transform hover:-translate-y-1`}
                        >
                            <div className='p-4 lg:p-6'>
                                <div className='flex items-start justify-between mb-3'>
                                    <div className={`p-2.5 rounded-xl ${card.bgColor} group-hover:scale-110 transition-transform duration-300 text-2xl`}>
                                        {card.icon}
                                    </div>
                                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${card.bgColor} ${card.textColor}`}>
                                        {loading ? '⏳' : '✅'}
                                    </span>
                                </div>
                                
                                <h3 className='text-sm font-medium text-gray-600 mb-1'>
                                    {card.title}
                                </h3>
                                
                                <p className='text-2xl lg:text-3xl font-bold text-gray-800'>
                                    {loading ? (
                                        <span className='inline-block w-20 h-8 bg-gray-200 animate-pulse rounded'></span>
                                    ) : (
                                        NumberTo2DP(card.value)
                                    )}
                                </p>
                                
                                <div className='mt-3 pt-3 border-t border-gray-100 flex items-center justify-between'>
                                    <span className='text-xs text-gray-400'>
                                        {card.subtitle}
                                    </span>
                                    {!loading && (
                                        <span className='text-xs text-green-500 flex items-center gap-1'>
                                            <span className='inline-block w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse'></span>
                                            Live
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Charts Section */}
            <div className='w-full mb-6 p-4 lg:p-6 bg-white rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-300'>
                <div className='flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4'>
                    <div className='flex items-center gap-3'>
                        <div className='w-1 h-8 bg-gradient-to-b from-[#069125] to-[#057a1f] rounded-full'></div>
                        <div>
                            <p className='text-lg font-bold text-gray-800'>
                                Patient Analytics
                            </p>
                            <p className='text-xs text-gray-400'>Patient registration trends</p>
                        </div>
                    </div>
                    <div className='flex items-center gap-2 mt-2 sm:mt-0'>
                        <span className='text-xs text-gray-400 bg-gray-50 px-3 py-1 rounded-full flex items-center gap-1'>
                            ⏱️ Real-time data
                        </span>
                    </div>
                </div>
                
            </div>

            {/* All Patients Table */}
            <div className='w-full'>
                <AllPatient />
            </div>

            <div className='w-full h-64 lg:h-80 mt-10'>
                    <PatientCharts />
                </div>
        </div>
    );
};

export default Patients;